import { useState } from "react";

import { FolderType } from "@/types/productTypes";
import { organizeFoldersIntoTree } from "../../../../utils/folderUtils";
import { Button } from "@/Components/ui/button";
import {
   Dialog,
   DialogContent,
   DialogHeader,
   DialogTitle,
   DialogFooter,
} from "@/Components/ui/dialog";
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from "@/Components/ui/select";
import ImageUploader from "../image/ImageUploader";
import FolderItem from "./FolderItem";
import { Input } from "@/Components/ui/input";
import { FolderPlus } from "lucide-react";

interface FolderSidebarProps {
   folders: FolderType[];
   onDropImage: (folderId: number, imageId: number) => void;
   selectedFolder: number | null;
   onSelectFolder: (folder: FolderType) => void;
   onCreateFolder: (name: string, parentId?: number) => void;
}

export default function FolderSidebar({
   folders,
   onDropImage,
   selectedFolder,
   onSelectFolder,
   onCreateFolder,
}: FolderSidebarProps) {
   const [isDialogOpen, setDialogOpen] = useState(false);
   const [newFolderName, setNewFolderName] = useState("");
   const [parentFolderId, setParentFolderId] = useState<string>("root");

   // Organize folders into a tree structure
   const folderTree = organizeFoldersIntoTree(folders);

   const renderFolderTree = (folders: FolderType[]) => {
      return folders.map((folder) => (
         <FolderItem
            key={folder.id}
            folder={folder}
            onDrop={onDropImage}
            selected={selectedFolder === folder.id}
            onSelect={onSelectFolder}
         />
      ));
   };

   const handleCreate = () => {
      if (newFolderName.trim().length > 0) {
         onCreateFolder(
            newFolderName,
            parentFolderId && parentFolderId !== "root"
               ? parseInt(parentFolderId)
               : undefined
         );
         setNewFolderName("");
         setParentFolderId("root");
         setDialogOpen(false);
      }
   };

   // Flatten folders for the select dropdown
   const flattenFolders = (folders: FolderType[], level = 0): JSX.Element[] => {
      return folders.flatMap((folder) => [
         <SelectItem key={folder.id} value={folder.id.toString()}>
            {"\u00A0".repeat(level * 2)}
            {folder.name}
         </SelectItem>,
         ...(folder.children ? flattenFolders(folder.children, level + 1) : []),
      ]);
   };

   return (
      <div className="w-72 p-4 border-r border-gray-200 flex flex-col h-full">
         <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Folders</h2>
            <Button size="icon" onClick={() => setDialogOpen(true)}>
               <FolderPlus className="h-5 w-5" />
            </Button>
         </div>

         <div className="overflow-y-auto flex-1">
            {renderFolderTree(folderTree)}
         </div>

         <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent>
               <DialogHeader>
                  <DialogTitle>Create New Folder</DialogTitle>
               </DialogHeader>
               <div className="space-y-4">
                  <Input
                     placeholder="Folder Name"
                     value={newFolderName}
                     onChange={(e) => setNewFolderName(e.target.value)}
                  />
                  <Select
                     value={parentFolderId}
                     onValueChange={setParentFolderId}
                  >
                     <SelectTrigger>
                        <SelectValue placeholder="Parent Folder (optional)" />
                     </SelectTrigger>
                     <SelectContent>
                        <SelectItem value="root">No parent</SelectItem>
                        {flattenFolders(folderTree)}
                     </SelectContent>
                  </Select>
               </div>
               <DialogFooter>
                  <Button
                     onClick={handleCreate}
                     disabled={!newFolderName.trim()}
                  >
                     Create Folder
                  </Button>
               </DialogFooter>
            </DialogContent>
         </Dialog>

         <div className="mt-4">
            <ImageUploader />
         </div>
      </div>
   );
}
